import React, { useEffect, useMemo, useState } from 'react';
import CircularProgress from '@mui/material/CircularProgress';
import {
  IconX,
  IconMail,
  IconUserPlus,
  IconTrash,
  IconChevronDown,
} from '../../pages/credit-plans/SVGIcons';
import LicenseBadge, { LicenseType } from '../../pages/teams/LicenseBadge';

interface InviteEntry {
  email: string;
  license: LicenseType;
}

interface InviteModalProps {
  open: boolean;
  onClose: () => void;
  setInviteMode?: any;
  sendInvite?: any;
  licenseInventory?: Partial<
    Record<LicenseType, { total: number; used: number }>
  >;
  existingPendingInvites?: Array<{
    email?: string;
    preassigned_license_type?: string;
  }>;
}

const normalizeLicenseType = (value: string | undefined): LicenseType => {
  const normalized = String(value ?? '')
    .toLowerCase()
    .trim();
  if (
    normalized === 'standard' ||
    normalized === 'pro' ||
    normalized === 'business' ||
    normalized === 'enterprise'
  ) {
    return normalized;
  }
  return 'none';
};

const InviteModal: React.FC<InviteModalProps> = ({
  open,
  onClose,
  setInviteMode,
  sendInvite,
  licenseInventory,
  existingPendingInvites = [],
}: InviteModalProps) => {
  const [email, setEmail] = useState('');
  const [selectedLicense, setSelectedLicense] = useState<LicenseType>('none');
  const [showLicensePicker, setShowLicensePicker] = useState(false);
  const [inviteList, setInviteList] = useState<InviteEntry[]>([]);
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isValidEmail = (val: string) =>
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val.trim());

  const normalizeEmails = (value: string) => {
    return value
      .split(/[\s,;]+/)
      .map((nextEmail) => nextEmail.trim())
      .filter(Boolean);
  };

  const safeInventory = useMemo<
    Record<LicenseType, { total: number; used: number }>
  >(
    () => ({
      standard: licenseInventory?.standard ?? { total: 0, used: 0 },
      pro: licenseInventory?.pro ?? { total: 0, used: 0 },
      business: licenseInventory?.business ?? { total: 0, used: 0 },
      enterprise: licenseInventory?.enterprise ?? { total: 0, used: 0 },
      none: { total: Number.POSITIVE_INFINITY, used: 0 },
    }),
    [licenseInventory],
  );

  const reservedPendingByLicense = useMemo<Record<LicenseType, number>>(() => {
    return existingPendingInvites.reduce(
      (acc, invite) => {
        const license = normalizeLicenseType(invite?.preassigned_license_type);
        if (license !== 'none') {
          acc[license] += 1;
        }
        return acc;
      },
      { standard: 0, pro: 0, business: 0, enterprise: 0, none: 0 },
    );
  }, [existingPendingInvites]);

  const inviteListCountByLicense = useMemo<Record<LicenseType, number>>(() => {
    return inviteList.reduce(
      (acc, invite) => {
        if (invite.license !== 'none') {
          acc[invite.license] += 1;
        }
        return acc;
      },
      { standard: 0, pro: 0, business: 0, enterprise: 0, none: 0 },
    );
  }, [inviteList]);

  const availableLicenseOptions = useMemo<LicenseType[]>(() => {
    const purchasable: LicenseType[] = [
      'standard',
      'pro',
      'business',
      'enterprise',
    ];
    const options = purchasable.filter((license) => {
      const total = Number(safeInventory[license]?.total ?? 0);
      const used = Number(safeInventory[license]?.used ?? 0);
      const reserved = Number(reservedPendingByLicense[license] ?? 0);
      const staged = Number(inviteListCountByLicense[license] ?? 0);
      return total - used - reserved - staged > 0;
    });

    return [...options, 'none'];
  }, [inviteListCountByLicense, reservedPendingByLicense, safeInventory]);

  useEffect(() => {
    if (
      selectedLicense !== 'none' &&
      !availableLicenseOptions.includes(selectedLicense)
    ) {
      setSelectedLicense('none');
    }
  }, [availableLicenseOptions, selectedLicense]);

  const closeModal = () => {
    if (isSubmitting) {
      return;
    }
    setShowLicensePicker(false);
    onClose?.();
    if (setInviteMode) {
      setInviteMode(false);
    }
  };

  const buildInviteDraft = () => {
    const parsedEmails = normalizeEmails(email);
    if (parsedEmails.length === 0) {
      return inviteList;
    }

    const seen = new Set(inviteList.map((entry) => entry.email.toLowerCase()));
    const draftToAdd: InviteEntry[] = [];

    for (const nextEmail of parsedEmails) {
      const lowered = nextEmail.toLowerCase();
      if (seen.has(lowered)) {
        continue;
      }
      draftToAdd.push({ email: nextEmail, license: selectedLicense });
      seen.add(lowered);
    }

    return [...inviteList, ...draftToAdd];
  };

  const getLicenseOverageMessage = (entries: InviteEntry[]) => {
    const inviteCountByLicense = entries.reduce(
      (acc, entry) => {
        if (entry.license !== 'none') {
          acc[entry.license] += 1;
        }
        return acc;
      },
      { standard: 0, pro: 0, business: 0, enterprise: 0, none: 0 },
    );

    for (const license of [
      'standard',
      'pro',
      'business',
      'enterprise',
    ] as LicenseType[]) {
      const total = Number(safeInventory[license]?.total ?? 0);
      const used = Number(safeInventory[license]?.used ?? 0);
      const reserved = Number(reservedPendingByLicense[license] ?? 0);
      const requested = Number(inviteCountByLicense[license] ?? 0);
      const available = Math.max(0, total - used - reserved);

      if (requested > available) {
        return `Only ${available} ${license} license${
          available === 1 ? '' : 's'
        } available`;
      }
    }

    return '';
  };

  const handleAdd = () => {
    const parsedEmails = normalizeEmails(email);

    if (parsedEmails.length === 0) {
      setError('Enter an email address');
      return;
    }

    const seen = new Set(inviteList.map((entry) => entry.email.toLowerCase()));
    const nextEntries: InviteEntry[] = [];

    for (const nextEmail of parsedEmails) {
      if (!isValidEmail(nextEmail)) {
        setError('Invalid email format');
        return;
      }

      const lowered = nextEmail.toLowerCase();
      if (seen.has(lowered)) {
        continue;
      }

      seen.add(lowered);
      nextEntries.push({ email: nextEmail, license: selectedLicense });
    }

    if (nextEntries.length === 0) {
      setError('Email already added');
      return;
    }

    const nextInviteList = [...inviteList, ...nextEntries];
    const overageMessage = getLicenseOverageMessage(nextInviteList);
    if (overageMessage) {
      setError(overageMessage);
      return;
    }

    setInviteList(nextInviteList);
    setEmail('');
    setSelectedLicense('none');
    setShowLicensePicker(false);
    setError('');
  };

  const handleRemove = (idx: number) => {
    setInviteList((prev) => prev.filter((_, i) => i !== idx));
    setError('');
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      handleAdd();
    }
  };

  const draftInviteCount = normalizeEmails(email).length;

  const handleSendInvites = async () => {
    if (isSubmitting || !sendInvite) {
      return;
    }

    const finalInviteList = buildInviteDraft();
    if (finalInviteList.length === 0) {
      setError('Add at least one invite before sending');
      return;
    }

    const invalidInvite = finalInviteList.find(
      (entry) => !isValidEmail(entry.email),
    );
    if (invalidInvite) {
      setError(`Invalid email format: ${invalidInvite.email}`);
      return;
    }

    const overageMessage = getLicenseOverageMessage(finalInviteList);
    if (overageMessage) {
      setError(overageMessage);
      return;
    }

    setIsSubmitting(true);

    try {
      const sendResult = await sendInvite({
        email: '',
        emails: finalInviteList.map((entry) => entry.email),
        invites: finalInviteList,
      });

      const wasSuccessful =
        sendResult === true ||
        (typeof sendResult === 'object' && sendResult?.success === true);

      if (wasSuccessful) {
        setInviteList([]);
        setEmail('');
        setSelectedLicense('none');
        setError('');
        closeModal();
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      closeModal();
    }
  };

  if (!open) return null;

  return (
    <div className='tm-modal-overlay' onClick={handleOverlayClick}>
      <div className='tm-modal'>
        {/* Header */}
        <div className='tm-modal-header'>
          <div className='tm-modal-title-row'>
            <IconUserPlus />
            <h3 className='tm-modal-title'>Invite Team Members</h3>
          </div>
          <button className='tm-action-btn' onClick={closeModal}>
            <IconX />
          </button>
        </div>

        {/* Input area */}
        <div className='tm-modal-body'>
          <label className='tm-modal-label'>Email address</label>
          <div className='tm-invite-input-row'>
            <div className='tm-invite-email-field'>
              <IconMail />
              <input
                className='tm-invite-input'
                type='email'
                placeholder='name@company.com'
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  setError('');
                }}
                onKeyDown={handleKeyDown}
              />
            </div>

            {/* License picker */}
            <div className='tm-invite-license-picker'>
              <button
                className='tm-btn tm-btn-outline tm-btn-sm'
                onClick={() => setShowLicensePicker((v) => !v)}
              >
                <LicenseBadge license={selectedLicense} /> <IconChevronDown />
              </button>
              {showLicensePicker && (
                <div className='tm-filter-dropdown tm-invite-license-dropdown'>
                  {availableLicenseOptions.map((l) => (
                    <button
                      key={l}
                      className={`tm-filter-option${
                        selectedLicense === l ? ' tm-filter-active' : ''
                      }`}
                      onClick={() => {
                        setSelectedLicense(l);
                        setShowLicensePicker(false);
                      }}
                    >
                      <LicenseBadge license={l} />
                    </button>
                  ))}
                </div>
              )}
            </div>

            <button
              className='tm-btn tm-btn-primary tm-btn-sm'
              onClick={handleAdd}
              disabled={isSubmitting}
            >
              Add
            </button>
          </div>
          {error && <p className='tm-invite-error'>{error}</p>}

          {/* Invite list */}
          <div className='tm-invite-list'>
            <label className='tm-modal-label'>
              Pending invites ({inviteList.length})
            </label>
            <div className='tm-invite-list-items'>
              {inviteList.map((entry, idx) => (
                <div key={idx} className='tm-invite-list-row'>
                  <span className='tm-invite-list-email'>{entry.email}</span>
                  <LicenseBadge license={entry.license} />
                  <button
                    className='tm-action-btn tm-action-btn-danger'
                    onClick={() => handleRemove(idx)}
                    title='Remove'
                    disabled={isSubmitting}
                  >
                    <IconTrash />
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className='tm-modal-footer'>
          <button className='tm-btn tm-btn-outline' onClick={closeModal}>
            Cancel
          </button>
          <button
            className='tm-btn tm-btn-primary'
            disabled={
              (inviteList.length === 0 && draftInviteCount === 0) ||
              isSubmitting
            }
            onClick={handleSendInvites}
          >
            {isSubmitting && (
              <CircularProgress size={16} style={{ color: '#fff' }} />
            )}{' '}
            <IconMail /> Send{' '}
            {inviteList.length + draftInviteCount > 0
              ? `${inviteList.length + draftInviteCount} `
              : ''}
            Invite
            {inviteList.length + draftInviteCount !== 1 ? 's' : ''}
          </button>
        </div>
      </div>
    </div>
  );
};

export default InviteModal;
